import { getPlayableUrlWithToken } from "connection/mux";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { getSessionStorageObject, setSessionStorageObject } from "utils/session-storage";

interface VideoSecurityGateProps {
	playbackUrl: string | undefined | null;
	drmExpected: boolean | undefined;
	children: React.ReactNode | React.ReactNode[] | ((state: VideoSecurityGateState) => React.ReactNode | React.ReactNode[]);
	fallback?: React.ReactNode;
	muxToken?: string;
	eventUuid?: string;
}

type VideoSecurityGateState = {
	url: string | undefined | null;
	drmToken: string | undefined;
	drmRequired: boolean | undefined;
	playbackId: string | undefined;
	loading: boolean;
}

const initialState: VideoSecurityGateState = {
	url: undefined,
	drmToken: undefined,
	drmRequired: undefined,
	playbackId: undefined,
	loading: true,
};

export const VideoSecurityGateContext = createContext<VideoSecurityGateState>(initialState);

export default function VideoSecurityGate({ playbackUrl, drmExpected, children, fallback, muxToken, eventUuid }: VideoSecurityGateProps) {
	const [state, setState] = useState<VideoSecurityGateState>(initialState);

	const loadTokens = useCallback(async () => {
		try {
			const stored = getSessionStorageObject(`${playbackUrl}`);
			if (stored) {
				setState({ ...stored, loading: false });
				return;
			}

			if (muxToken) {
				// if there is a mux token passed, optimistically allow passage so there's less load time for the player
				// if the token is bad, the response will still catch it and update the playback url to be the real one
				setState(prev => ({ ...prev, loading: false, url: playbackUrl + '?token=' + muxToken, ok: true }));
			} else { 
				setState(prev => ({ ...prev, loading: true }));
			}

			const response = await getPlayableUrlWithToken(playbackUrl || "", drmExpected, muxToken, eventUuid);

			setState({
				...response,
				loading: false
			});

			if (response.ok) {
				setSessionStorageObject(`${playbackUrl}`, response);
			}
		} catch (e) {
			console.error("Error loading Mux player tokens:", e);
			setState(initialState);
		} finally {
			setState(prev => ({ ...prev, loading: false }));
		}
	}, [playbackUrl, drmExpected, muxToken, eventUuid]);

	useEffect(() => {
		loadTokens();
	}, [loadTokens]);

	const content = () => {
		if (state.loading) {
			return fallback || null;
		}

		if (typeof children === "function") {
			return children(state);
		}

		return children;
	};

	return (
		<VideoSecurityGateContext.Provider value={state}>
			{content()}
		</VideoSecurityGateContext.Provider>
	);
}
